import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ToastManager } from "@tvg/design-system";
import { isTvg5 } from "@tvg/utils/generalUtils";
import { BetaFeature, DismissOrigin } from "@tvg/sh-lib-beta-program/types";
import { PAGE } from "../../types";
import {
  getBetaProgramContent,
  getBetaProgramModal,
  getBetaWhitelist,
  getUser,
  getAlertMessages
} from "../../redux/selectors";
import {
  buildCTA,
  executeGtmByView,
  goToPage,
  handleOnClose,
  openModalOnMount
} from "./utils";
import { QA_LABELS } from "../../utils/constants";
import { SplashScreen, WelcomeModal } from "../../components";
import { alertMessages } from "../../redux/defaultValues";
import { useBetaFeatureInfo } from "../BetaFeaturesModal/hooks";
import { fetchStoryblokFeature } from "../BetaFeaturesModal/utils";
import { BetaFeature as StoryblokBetaFeature } from "../BetaFeaturesModal/types";

interface Props {
  betaFeature: BetaFeature;
  redirectUrlAfterEnrollment?: string;
}

const BetaProgramModal = ({ betaFeature }: Props) => {
  // HOOKS
  const dispatch = useDispatch();
  const [page, setPage] = useState<PAGE>(
    !isTvg5() ? PAGE.ENROLL : PAGE.WELCOME
  );
  const [betaFeatureInfo, setBetaFeatureInfo] =
    useState<StoryblokBetaFeature | null>(null);

  // SELECTORS
  const { isOpen } = useSelector(getBetaProgramModal);
  const whitelist = useSelector(getBetaWhitelist);
  const welcomeConfig = useSelector(getBetaProgramContent);
  const user = useSelector(getUser);
  const contentAlertMessages = useSelector(getAlertMessages);

  // VARIABLES
  const shouldOpen = isOpen && user.isLogged;
  const showBackArrow = page === PAGE.LEARN_MORE;
  const { welcome } = useBetaFeatureInfo({
    selectedFeature: betaFeature,
    isUserEnrolled: true,
    noFeaturesAvailable: false,
    betaFeatures: {
      [betaFeature]: betaFeatureInfo
    }
  });

  useEffect(
    openModalOnMount(
      page,
      user,
      whitelist,
      dispatch,
      betaFeature,
      betaFeatureInfo
    ),
    [JSON.stringify(whitelist), betaFeatureInfo]
  );
  useEffect(executeGtmByView(isOpen, page, betaFeature), [page, isOpen]);

  useEffect(() => {
    fetchStoryblokFeature(betaFeature).then((featureFormatted) => {
      setBetaFeatureInfo(featureFormatted);
    });
  }, []);

  return (
    <>
      <SplashScreen
        isOpen={shouldOpen}
        title={welcome?.title}
        subtitle={welcome?.subtitle}
        onClose={handleOnClose(
          DismissOrigin.CROSS,
          page,
          dispatch,
          user.accountNumber,
          betaFeature,
          whitelist,
          contentAlertMessages.dismiss?.description
        )}
        onOverlayClick={handleOnClose(
          DismissOrigin.OVERLAY,
          page,
          dispatch,
          user.accountNumber,
          betaFeature,
          whitelist,
          contentAlertMessages.dismiss?.description
        )}
        {...(showBackArrow && { onBack: goToPage(PAGE.ENROLL, setPage) })}
      >
        <WelcomeModal
          actions={buildCTA(
            welcomeConfig.welcomeMessage?.actions,
            page,
            setPage,
            dispatch,
            user.accountNumber,
            whitelist,
            betaFeature,
            contentAlertMessages.dismiss?.description ||
              alertMessages.dismiss?.description ||
              ""
          )}
          details={welcome?.description}
        />
      </SplashScreen>
      {!isTvg5() && page !== PAGE.WELCOME && (
        <ToastManager
          qaLabel={QA_LABELS.DISMISS_TOAST}
          ref={ToastManager.setRef}
          message={
            contentAlertMessages.dismiss?.description ||
            alertMessages.dismiss?.description ||
            ""
          }
          variant="informational"
          hasDismissButton
        />
      )}
    </>
  );
};
export default React.memo(BetaProgramModal);
